import { EventEmitter } from 'events';

import type { EventNamesBase } from './types';

export class MemorizedEventEmitter<EventNames extends EventNamesBase> extends EventEmitter {
  protected firedEvents = new Map<EventNames, unknown>();

  protected saveEvent(eventName: EventNames, arg: unknown): void {
    this.firedEvents.set(eventName, arg);
  }

  emit(eventName: EventNames, arg?: unknown): boolean {
    this.saveEvent(eventName, arg);
    return super.emit(eventName, arg);
  }

  /**
   * If the specified event was already emitted this method will call the listener immediately.
   * @param eventName
   * @param listener
   * @returns
   */
  protected callLastListener(eventName: EventNames, listener: (event: unknown) => void): boolean {
    const lastEvent = this.firedEvents.get(eventName);
    if (lastEvent) {
      listener(lastEvent);
      return true;
    }
    return false;
  }

  /**
   * Adds the @listener function to the end of the listeners array for the event named @eventName Will ensure that only the listener is added only once. If the specified event was already emitted this method will call the listener immediately and add the listener afterwards.
   * @param eventName
   * @param listener
   * @returns
   */
  done(eventName: EventNames, listener: (event: unknown) => void): this {
    this.callLastListener(eventName, listener);
    this.addListener(eventName, listener);
    return this;
  }

  /**
   * Adds a one-time listener function for the event named @eventName The next time @eventName is triggered, this listener is removed and then invoked. If the specified event was already emitted this method will not add an event listener and instead call the listener immediately.
   * @param eventName
   * @param listener
   * @returns
   */
  doneOnce(eventName: EventNames, listener: (event: unknown) => void): this {
    const calledLastListener = this.callLastListener(eventName, listener);
    if (!calledLastListener) {
      this.once(eventName, listener);
    }
    return this;
  }

  /**
   * Be careful, as it will forget the fired events
   * @returns
   */
  forgetEvent(eventName: EventNames): this {
    this.firedEvents.delete(eventName);
    return this;
  }
}
